// Campaign API service for tezda-marketing backend
import { fetchWithAuth } from '../utils/auth';

// Marketing API base URL
export const getMarketingApiBaseUrl = (): string => {
  return 'https://mark.tezda.tech';
};

// Campaign types
export type CampaignType = 'ONE_TIME' | 'RECURRING' | 'TRIGGERED';
export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
export type Platform = 'ios' | 'android' | 'all';
export type TriggerType =
  | 'STORY_CREATED' | 'STORY_EXPIRING_SOON' | 'STORY_EXPIRED'
  | 'PING_CREATED' | 'PING_MILESTONE' | 'PING_TRENDING'
  | 'USER_INACTIVE' | 'USER_NEW' | 'USER_MILESTONE'
  | 'DM_UNREAD_PENDING' | 'FOLLOW_RECEIVED' | 'LIKE_RECEIVED' | 'COMMENT_RECEIVED';

export interface SegmentFilter {
  field: string;
  operator: 'EQUALS' | 'NOT_EQUALS' | 'GREATER_THAN' | 'LESS_THAN' | 'IN' | 'NOT_IN' | 'CONTAINS' | 'EXISTS' | 'BETWEEN';
  value: any;
}

export interface SegmentRule {
  operator: 'AND' | 'OR';
  filters: SegmentFilter[];
}

export interface InlineTemplate {
  title: string;
  body: string;
  imageUrl?: string;
  actionType?: string;
  actionUrl?: string;
  contentType?: string;
  contentConfig?: Record<string, any>;
}

export interface Schedule {
  type: 'FIXED_TIMEZONE' | 'USER_TIMEZONE';
  timezone?: string;
  time: string;
  frequency?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  daysOfWeek?: number[];
  dayOfMonth?: number;
  startDate: string;
  endDate?: string;
  sendNow?: boolean; // When true, campaign is sent immediately
}

export interface Trigger {
  type: TriggerType;
  delayMinutes?: number;
  conditions?: Record<string, any>;
  milestoneValues?: number[];
}

export interface RateLimit {
  maxPerDay?: number;
  maxPerCampaign?: number;
  minHoursBetween?: number;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  dedupeWindowHours?: number;
}

export interface CampaignVariant {
  name: string;
  weight: number;
  templateId?: string;
  inlineTemplate?: InlineTemplate;
}

export interface CampaignStats {
  totalTargeted?: number;
  totalSent?: number;
  totalDelivered?: number;
  totalOpened?: number;
  totalClicked?: number;
  totalFailed?: number;
  totalSkipped?: number;
}

// Per-language push text (tezda-marketing V2, PUT /v2/campaigns/:id/translations).
// The campaign's inlineTemplate title/body stays the default every other device gets.
export const CAMPAIGN_LANGUAGES = [
  { code: 'ru', label: 'Russian' },
  { code: 'uz', label: 'Uzbek (Latin)' },
  { code: 'uz_Cyrl', label: 'Uzbek (Cyrillic)' },
  { code: 'kk', label: 'Kazakh' },
  { code: 'ky', label: 'Kyrgyz' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ur', label: 'Urdu' },
  { code: 'hi', label: 'Hindi' },
  { code: 'zh', label: 'Chinese' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'fil', label: 'Filipino' },
  { code: 'ceb', label: 'Cebuano' },
  { code: 'en', label: 'English' },
] as const;

export type CampaignLanguage = (typeof CAMPAIGN_LANGUAGES)[number]['code'];

export interface CampaignTranslation {
  title: string;
  body: string;
}

export type CampaignTranslations = Partial<Record<CampaignLanguage, CampaignTranslation>>;

// Limits the V2 route enforces (title 1-200, body 1-1000, neither blank).
export const TRANSLATION_TITLE_MAX = 200;
export const TRANSLATION_BODY_MAX = 1000;

export interface Campaign {
  _id?: string;
  name: string;
  description?: string;
  type: CampaignType;
  status?: CampaignStatus;
  segmentId?: string;
  segmentRule?: SegmentRule;
  targetPlatforms?: Platform[];
  templateId?: string;
  inlineTemplate?: InlineTemplate;
  translations?: CampaignTranslations;
  schedule?: Schedule;
  trigger?: Trigger;
  rateLimit?: RateLimit;
  variants?: CampaignVariant[];
  priority?: number;
  tags?: string[];
  stats?: CampaignStats;
  nextRunAt?: string;
  lastRunAt?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignListResponse {
  campaigns: Campaign[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RunResponse {
  message: string;
  runId: string;
}

// API Functions
export const fetchCampaigns = async (params?: {
  status?: CampaignStatus;
  type?: CampaignType;
  tag?: string;
  page?: number;
  limit?: number;
}): Promise<CampaignListResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.type) queryParams.set('type', params.type);
  if (params?.tag) queryParams.set('tag', params.tag);
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());

  const url = `${getMarketingApiBaseUrl()}/campaigns?${queryParams.toString()}`;
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch campaigns' }));
    throw new Error(error.error || 'Failed to fetch campaigns');
  }

  return response.json();
};

export const fetchCampaign = async (id: string): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}`;
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Campaign not found' }));
    throw new Error(error.error || 'Campaign not found');
  }

  return response.json();
};

export const createCampaign = async (campaign: Omit<Campaign, '_id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(campaign),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create campaign' }));
    throw new Error(error.error || 'Failed to create campaign');
  }

  return response.json();
};

export const updateCampaign = async (id: string, campaign: Partial<Campaign>): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}`;
  const response = await fetchWithAuth(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(campaign),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update campaign' }));
    throw new Error(error.error || 'Failed to update campaign');
  }

  return response.json();
};

export const deleteCampaign = async (id: string): Promise<void> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}`;
  const response = await fetchWithAuth(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete campaign' }));
    throw new Error(error.error || 'Failed to delete campaign');
  }
};

export const scheduleCampaign = async (id: string): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/schedule`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to schedule campaign' }));
    throw new Error(error.error || 'Failed to schedule campaign');
  }

  return response.json();
};

export const pauseCampaign = async (id: string): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/pause`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to pause campaign' }));
    throw new Error(error.error || 'Failed to pause campaign');
  }

  return response.json();
};

export const resumeCampaign = async (id: string): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/resume`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to resume campaign' }));
    throw new Error(error.error || 'Failed to resume campaign');
  }

  return response.json();
};

export const cancelCampaign = async (id: string): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/cancel`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to cancel campaign' }));
    throw new Error(error.error || 'Failed to cancel campaign');
  }

  return response.json();
};

export const runCampaign = async (id: string): Promise<RunResponse> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/run`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to run campaign' }));
    throw new Error(error.error || 'Failed to run campaign');
  }

  return response.json();
};

export interface DirectSendResponse {
  message: string;
  runId: string;
  stats: {
    targeted: number;
    sent: number;
    failed: number;
    errors?: string[];
  };
}

// Send campaign directly without SQS (for testing/immediate send)
export const sendCampaignDirect = async (id: string): Promise<DirectSendResponse> => {
  const url = `${getMarketingApiBaseUrl()}/campaigns/${id}/send-direct`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to send campaign' }));
    throw new Error(error.error || 'Failed to send campaign');
  }

  return response.json();
};

// True when the campaign has at least one per-language variant to send.
export const hasTranslations = (translations?: CampaignTranslations): boolean =>
  !!translations && Object.keys(translations).length > 0;

// Replace the campaign's per-language text (the whole map). Only DRAFT or SCHEDULED campaigns.
export const saveCampaignTranslations = async (
  id: string,
  translations: CampaignTranslations
): Promise<Campaign> => {
  const url = `${getMarketingApiBaseUrl()}/v2/campaigns/${id}/translations`;
  const response = await fetchWithAuth(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ translations }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to save translations' }));
    throw new Error(error.error || 'Failed to save translations');
  }

  return response.json();
};

// Send now, each device in its app language when the campaign has that variant (V2 send-direct).
export const sendCampaignDirectV2 = async (id: string): Promise<DirectSendResponse> => {
  const url = `${getMarketingApiBaseUrl()}/v2/campaigns/${id}/send-direct`;
  const response = await fetchWithAuth(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to send campaign' }));
    throw new Error(error.error || 'Failed to send campaign');
  }

  return response.json();
};

// Image upload response
export interface ImageUploadResponse {
  success: boolean;
  imageUrl: string;
  dimensions: {
    width: number;
    height: number;
  };
  size: number;
}

// Image upload requirements
export interface ImageRequirements {
  maxFileSize: string;
  maxFileSizeBytes: number;
  allowedTypes: string[];
  recommendedDimensions: {
    width: number;
    height: number;
    aspectRatio: string;
  };
  notes: string[];
}

// Upload campaign image to S3
export const uploadCampaignImage = async (file: File): Promise<ImageUploadResponse> => {
  const url = `${getMarketingApiBaseUrl()}/upload/campaign-image`;

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: formData,
    // Don't set Content-Type header - browser will set it with boundary for multipart
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to upload image' }));
    throw new Error(error.error || 'Failed to upload image');
  }

  return response.json();
};

// Get image upload requirements
export const getImageRequirements = async (): Promise<ImageRequirements> => {
  const url = `${getMarketingApiBaseUrl()}/upload/requirements`;
  const response = await fetchWithAuth(url);

  if (!response.ok) {
    throw new Error('Failed to fetch image requirements');
  }

  return response.json();
};

// Status badge colors
export const getStatusColor = (status?: CampaignStatus): string => {
  switch (status) {
    case 'DRAFT': return 'bg-gray-500';
    case 'SCHEDULED': return 'bg-blue-500';
    case 'RUNNING': return 'bg-green-500';
    case 'PAUSED': return 'bg-yellow-500';
    case 'COMPLETED': return 'bg-purple-500';
    case 'CANCELLED': return 'bg-red-500';
    default: return 'bg-gray-400';
  }
};

// Type badge colors
export const getTypeColor = (type: CampaignType): string => {
  switch (type) {
    case 'ONE_TIME': return 'bg-indigo-500';
    case 'RECURRING': return 'bg-teal-500';
    case 'TRIGGERED': return 'bg-orange-500';
    default: return 'bg-gray-400';
  }
};
